import { useState, useEffect } from "react";
import { api, gr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

export default function MarinaInquiryDialog({ marina, context, open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) { setForm({ name: "", phone: "", email: "", message: "" }); setErr(""); setDone(null); }
  }, [open]);

  if (!marina) return null;
  const loc = [marina.address, marina.city, marina.postal_code].filter(Boolean).join(", ");

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) return setErr("Συμπληρώστε το όνομά σας.");
    if (!form.email.trim()) return setErr("Συμπληρώστε το email σας — εκεί θα σταλεί ο κωδικός.");
    setSending(true);
    try {
      const res = (await api.post("/public/inquiries", {
        marina_id: marina.marina_id,
        visitor_name: form.name.trim(),
        visitor_phone: form.phone.trim(),
        visitor_email: form.email.trim(),
        message: form.message.trim(),
        length: context?.length ? Number(context.length) : undefined,
        start_date: context?.startDate || undefined,
        end_date: context?.endDate || undefined,
      })).data;
      setDone(res.ref_code);
    } catch (e) {
      setErr(e.response?.data?.detail || "Κάτι πήγε στραβά.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="marina-inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-[#234E7D]">{marina.name}</DialogTitle>
          <DialogDescription>{loc || "—"}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3 py-2 text-center" data-testid="inquiry-success">
            <CheckCircle size={44} weight="fill" className="mx-auto text-[#10B981]" />
            <div className="font-semibold">Το αίτημά σας στάλθηκε!</div>
            <p className="text-sm text-muted-foreground">Κρατήστε τον κωδικό παρακολούθησης για να δείτε την απάντηση του διαχειριστή:</p>
            <div className="mx-auto inline-block rounded-sm border border-dashed border-[#0055FF] bg-[#EFF6FF] px-4 py-2 font-head text-2xl font-bold tracking-widest text-[#0044CC]" data-testid="inquiry-ref-code">{done}</div>
            <Button className="mt-2 w-full rounded-sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="inquiry-close">Κλείσιμο</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {(marina.phone || marina.email) && (
              <div className="rounded-sm bg-secondary/60 p-3 text-sm">
                <div className="flex items-center gap-1 text-muted-foreground"><Phone size={14} /> {[marina.phone, marina.email].filter(Boolean).join(" · ")}</div>
              </div>
            )}
            {marina.services?.length > 0 && (
              <div>
                <div className="text-xs font-semibold tracking-wider text-muted-foreground">Υπηρεσίες</div>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="marina-services">
                  {marina.services.map((s, i) => (
                    <span key={i} className="rounded-sm border border-border bg-white px-2 py-0.5 text-xs">{s.name}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-sm border border-border p-3">
              <div className="text-xs font-semibold tracking-wider text-muted-foreground">Το αίτημά σας</div>
              {(context?.length || context?.startDate) && (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="inquiry-context">
                  Σκάφος {context?.length}μ · {gr(context?.startDate)} → {gr(context?.endDate)}
                </p>
              )}
              <div className="mt-2 grid gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Όνομα *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="inquiry-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Τηλέφωνο</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="inquiry-phone" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="inquiry-email" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">Ο κωδικός παρακολούθησης θα σταλεί στο email σας.</p>
                <div>
                  <Label className="text-xs text-muted-foreground">Μήνυμα</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 rounded-sm" rows={3} placeholder="Πείτε μας για το σκάφος σας ή τυχόν ερωτήσεις…" data-testid="inquiry-message" />
                </div>
              </div>
            </div>

            {err && <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive" data-testid="inquiry-error">{err}</div>}
            <Button disabled={sending} onClick={submit} className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="inquiry-submit">
              {sending ? "Αποστολή…" : (<><PaperPlaneTilt size={18} weight="bold" /> Αποστολή αιτήματος</>)}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
